import React, { FC, useRef, useState } from "react";
import { Button, Modal, message, Typography } from "antd";
import { useMutation, useQuery } from "@apollo/client";
import { CreateModal, FormValues } from "./create-modal";
import { PracticeAreaDefaultModal } from "./practice-area-default-modal";
import { GroupedEntityType, OutputDesignType } from "~/gql/graphql";
import { VpdDesignEditor } from "./vpd-design-editor";
import {
  cloneDesignMutation,
  createDesignMutation,
  deleteDesignMutation,
  filteredDesignsQuery,
  updateDesignMutation,
  RESET_PROPOSAL_OUTPUTS,
  getClientReportMergeTagsQuery,
  getProposalMergeTagsQuery,
} from "./graphql-queries";
import { ORGANISATIONAL_UNITS_QUERY } from "~/graphql/query";

import Handlebars from "handlebars";
import { VpdScrollable } from "~/design-system";
import { GroupItems } from "../groups/group-item";
import { DesignThumbnails } from "./design-thumbnails";
import { LoadingSkeleton } from "../page-loading/skeleton/loading-skeleton";

type DesignEditorProps = {
  outputDesignType: OutputDesignType;
  groupedEntityType: GroupedEntityType;
};

export const DesignEditor: FC<DesignEditorProps> = ({ outputDesignType, groupedEntityType }) => {
  const [modal, contextHolder] = Modal.useModal();
  const downloadRef = useRef<HTMLAnchorElement | null>(null);
  const editorRef = useRef<any>(null);
  const [templateString, setTemplateString] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedGroupItemId, setSelectedGroupItemId] = useState<number[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<FormValues | null>(null);
  const [practiceAreaDefaultModalVisible, setPracticeAreaDefaultModalVisible] = useState(false);
  const [defaultForOrgUnitIds, setDefaultForOrgUnitIds] = useState<number[]>([]);

  const [loadingEditor, setLoadingEditor] = useState(true);

  const { data: allTemplates, variables: calledWithVariables } = useQuery(filteredDesignsQuery, {
    variables: {
      filter: {
        type: { equals: outputDesignType },
      },
    },
  });

  const { data: { organisationalUnits: practiceAreas = [] } = {} } = useQuery(
    ORGANISATIONAL_UNITS_QUERY
  );

  const proposalMergeTagsResult = useQuery(getProposalMergeTagsQuery, {
    skip: outputDesignType !== OutputDesignType.Proposal,
  });
  const clientReportMergeTagsResult = useQuery(getClientReportMergeTagsQuery, {
    skip: outputDesignType !== OutputDesignType.ClientReport,
  });

  const mergeTagFields =
    outputDesignType === OutputDesignType.Proposal
      ? proposalMergeTagsResult.data?.getProposalMergeTagFields
      : clientReportMergeTagsResult.data?.getClientReportMergeTagFields;

  const [createMatterTemplate] = useMutation(createDesignMutation);
  const [updateMatterTemplate] = useMutation(updateDesignMutation);
  const [deleteTemplate] = useMutation(deleteDesignMutation);
  const [cloneTemplate] = useMutation(cloneDesignMutation);
  const [resetExistingProposals] = useMutation(RESET_PROPOSAL_OUTPUTS);

  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [editingTemplateId, setEditingTemplateId] = useState<number | null>(null);

  const filteredDesigns =
    allTemplates?.filteredDesigns && selectedGroupItemId.length
      ? allTemplates?.filteredDesigns.filter((design) =>
          selectedGroupItemId
            .filter((id): id is number => id !== null)
            .includes(design.groupItemId as number)
        )
      : allTemplates?.filteredDesigns;

  const handleEditorInit = (editor: any) => {
    editorRef.current = editor;
    setLoadingEditor(false);
  };

  const save = () => {
    if (editorRef.current && selectedTemplateId) {
      const content = editorRef.current.getContent({ format: "html" });
      try {
        const result = Handlebars.parse(content);
        console.log({ result });
      } catch (error) {
        console.log({ error });
        if (typeof error === "object" && error && "message" in error) {
          message.error({ content: error.message, style: { marginTop: "20vh", height: 600 } });
        }
        return;
      }

      updateMatterTemplate({
        variables: {
          designType: outputDesignType,
          data: { data: { htmlContent: { set: content } }, id: selectedTemplateId },
        },
        update: (cache, { data }) => {
          const updatedDesigns = data?.updateDesigns;
          const existingTemplates = cache.readQuery({
            query: filteredDesignsQuery,
            variables: { filter: { type: { equals: outputDesignType } } },
          });
          if (existingTemplates && updatedDesigns) {
            cache.writeQuery({
              variables: { filter: { type: { equals: outputDesignType } } },
              query: filteredDesignsQuery,
              data: {
                filteredDesigns: [
                  ...(existingTemplates.filteredDesigns ?? []).map((x) => {
                    if (x.id === selectedTemplateId) {
                      return {
                        ...x,
                        htmlContent: content,
                      };
                    }
                    return x;
                  }),
                ],
              },
            });
          }
        },
      });
    }
  };

  const handlePracticeAreaDefaultSubmit = (defaultForOrgUnitIdsToConnect: number[]) => {
    if (defaultForOrgUnitIdsToConnect && editingTemplateId) {
      updateMatterTemplate({
        variables: {
          designType: outputDesignType,
          data: {
            data: {
              defaultForOrgUnits: {
                connect: defaultForOrgUnitIdsToConnect.map((id) => ({ id })),
                disconnect: defaultForOrgUnitIds
                  .filter((id) => !defaultForOrgUnitIdsToConnect.includes(id))
                  .map((id) => ({ id })),
              },
            },
            id: editingTemplateId,
          },
        },
        refetchQueries: ["FilteredDesigns"],
      });
    }
    setPracticeAreaDefaultModalVisible(false);
  };

  return (
    <>
      {contextHolder}
      <div style={{ height: "100%", display: "flex", flex: 1 }}>
        <div style={{ flex: 1, flexDirection: "column", height: "100%" }}>
          <div
            style={{
              maxHeight: "10%",
            }}
          >
            <Button type="primary" style={{ marginBottom: 5 }} onClick={() => setOpenModal(true)}>
              Create
            </Button>
            <div></div>
          </div>
          <div
            id="vpd-group-tree-area"
            style={{ maxHeight: "90%", maxWidth: "100%", minHeight: "500;" }}
          >
            <VpdScrollable>
              <div style={{ padding: 10, width: "100%", height: "90dvh" }}>
                <GroupItems
                  selectedTreeKeys={selectedGroupItemId}
                  onSelectTreeItem={(keys) => {
                    setSelectedGroupItemId(keys);
                    setSelectedTemplateId(null);
                  }}
                  groupItemEntityType={groupedEntityType}
                />
              </div>
            </VpdScrollable>
          </div>
        </div>

        <a
          ref={(e) => {
            downloadRef.current = e;
          }}
        />

        <div style={{ flex: 3 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <Button onClick={() => setSelectedTemplateId(null)}> Back</Button>
            </div>

            {selectedTemplateId && (
              <div style={{ flex: 1 }}>
                <Typography.Title
                  level={5}
                  style={{
                    margin: 0,
                    paddingLeft: "2rem",
                    paddingRight: "2rem",
                    paddingBottom: "0.2rem",
                  }}
                >
                  {filteredDesigns?.find((x) => x.id === selectedTemplateId)?.title}
                </Typography.Title>
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "row" }}>
              <Button
                onClick={() => {
                  modal.info({
                    width: "80%",
                    title: "Available Merge Tags",
                    content: (
                      <div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            marginBottom: "16px",
                          }}
                        >
                          <Button onClick={() => resetExistingProposals()}>
                            Reset Existing Proposals
                          </Button>
                        </div>
                        <Typography.Paragraph>
                          The following merge tags are available for use in your Handlebars
                          templates:
                        </Typography.Paragraph>
                        {mergeTagFields && mergeTagFields.length > 0 ? (
                          <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
                            <table
                              style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                marginTop: "16px",
                              }}
                            >
                              <thead>
                                <tr style={{ borderBottom: "2px solid #f0f0f0" }}>
                                  <th
                                    style={{
                                      textAlign: "left",
                                      padding: "8px 12px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Field Name
                                  </th>
                                  <th
                                    style={{
                                      textAlign: "left",
                                      padding: "8px 12px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Handlebars Syntax
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {mergeTagFields.map((tag, index) => (
                                  <tr
                                    key={index}
                                    style={{
                                      borderBottom: "1px solid #f0f0f0",
                                    }}
                                  >
                                    <td
                                      style={{
                                        padding: "8px 12px",
                                        verticalAlign: "top",
                                      }}
                                    >
                                      {tag.title}
                                    </td>
                                    <td
                                      style={{
                                        padding: "8px 12px",
                                        fontFamily: "monospace",
                                        backgroundColor: "#f5f5f5",
                                        verticalAlign: "top",
                                      }}
                                    >
                                      {`{{${tag.value}}}`}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <Typography.Text type="secondary">
                            No merge tags available. Please ensure the query has loaded
                            successfully.
                          </Typography.Text>
                        )}
                      </div>
                    ),
                    onOk() {},
                  });
                }}
                type="link"
              >
                Help
              </Button>

              <Button type="primary" onClick={save}>
                Save
              </Button>
            </div>
          </div>
          <div style={{ padding: 10, paddingRight: 0, height: "85dvh", overflowY: "scroll" }}>
            {selectedTemplateId && filteredDesigns?.find((x) => x.id === selectedTemplateId) ? (
              <>
                {loadingEditor && <LoadingSkeleton count={10} loading={loadingEditor} />}
                <VpdDesignEditor
                  outputDesignId={selectedTemplateId}
                  handleEditorInit={(editor) => handleEditorInit(editor)}
                  templateString={templateString}
                  removeTemplateMargin={
                    filteredDesigns.find((x) => x.id === selectedTemplateId)?.removeTemplateMargin
                  }
                  mergeTagFields={mergeTagFields ?? []}
                />
              </>
            ) : (
              filteredDesigns && (
                <DesignThumbnails
                  onDelete={(id) => {
                    if (
                      outputDesignType === OutputDesignType.Proposal ||
                      outputDesignType === OutputDesignType.ClientReport
                    ) {
                      const activeTemplateIds =
                        allTemplates?.filteredDesigns?.filter((x) => x.active)?.map((x) => x.id) ??
                        [];
                      if (activeTemplateIds.length === 1 && activeTemplateIds[0] === id) {
                        modal.warning({
                          title: "Warning",
                          content: `You must keep at least one active ${
                            outputDesignType === OutputDesignType.Proposal
                              ? "proposal"
                              : "client report"
                          } design.`,
                        });
                        return;
                      }
                    }
                    deleteTemplate({
                      variables: { id },
                      update: (cache) => {
                        const existingTemplates = cache.readQuery({
                          query: filteredDesignsQuery,
                          variables: calledWithVariables,
                        });
                        if (existingTemplates) {
                          cache.writeQuery({
                            query: filteredDesignsQuery,
                            variables: calledWithVariables,
                            data: {
                              filteredDesigns: [
                                ...(existingTemplates.filteredDesigns || []).filter(
                                  (x) => x.id !== id
                                ),
                              ],
                            },
                          });
                        }
                      },
                    });
                  }}
                  onEdit={(template) => {
                    setIsEditMode(true);
                    setInitialValues(template.formValues);
                    setEditingTemplateId(template.id);
                    setOpenModal(true);
                  }}
                  onSetPracticeAreaDefaults={(id, defaultForOrgUnitIds) => {
                    setPracticeAreaDefaultModalVisible(true);
                    setEditingTemplateId(id);
                    setDefaultForOrgUnitIds(defaultForOrgUnitIds);
                  }}
                  onSetDefault={(payload) => {
                    updateMatterTemplate({
                      variables: {
                        data: payload,
                        designType: outputDesignType,
                      },
                      refetchQueries: ["FilteredDesigns"],
                    });
                  }}
                  onSetActive={(payload, item) => {
                    if (
                      outputDesignType === OutputDesignType.Proposal ||
                      outputDesignType === OutputDesignType.ClientReport
                    ) {
                      const activeTemplateIds =
                        allTemplates?.filteredDesigns?.filter((x) => x.active)?.map((x) => x.id) ??
                        [];
                      if (activeTemplateIds.length === 1 && activeTemplateIds[0] === item.id) {
                        modal.warning({
                          title: "Warning",
                          content: `You must keep at least one active ${
                            outputDesignType === OutputDesignType.Proposal
                              ? "proposal"
                              : "client report"
                          } design.`,
                        });
                        return;
                      }
                    }
                    updateMatterTemplate({
                      variables: {
                        data: payload,
                        designType: outputDesignType,
                      },
                      update: (cache) => {
                        cache.updateQuery(
                          {
                            query: filteredDesignsQuery,
                            variables: calledWithVariables,
                          },
                          (currentData) => {
                            return {
                              filteredDesigns:
                                currentData?.filteredDesigns?.map((x) => {
                                  if (x.id === item.id) {
                                    return {
                                      ...x,
                                      active: !item.active,
                                    };
                                  }
                                  return x;
                                }) || [],
                            };
                          }
                        );
                      },
                    });
                  }}
                  handleCardClick={(id) => {
                    setSelectedTemplateId(id);
                    setTemplateString(filteredDesigns.find((x) => x.id === id)?.htmlContent ?? "");
                  }}
                  clone={(id) => {
                    cloneTemplate({
                      variables: {
                        id,
                      },
                      refetchQueries: [
                        {
                          query: filteredDesignsQuery,
                          variables: calledWithVariables,
                        },
                      ],
                    });
                  }}
                  templates={filteredDesigns.map((x) => ({
                    id: x.id,
                    title: x.title,
                    description: x.description,
                    html: x.htmlContent,
                    active: x.active,
                    type: x.type,
                    default: x.default,
                    defaultForOrgUnitIds: x.defaultForOrgUnits?.map((x) => x.id) ?? [],
                    groupItemId: x.groupItemId,
                  }))}
                />
              )
            )}
          </div>
        </div>
        <CreateModal
          onAddNewGroupItem={() => {
            console.log("Add new group item");
          }}
          validateTitle={(title) => {
            const alreadyExists =
              allTemplates?.filteredDesigns?.some(
                (x) => x.title === title && x.id !== editingTemplateId
              ) ?? false;
            return alreadyExists;
          }}
          visible={openModal}
          onCancel={() => {
            setOpenModal(false);
            setIsEditMode(false);
            setInitialValues(null);
          }}
          onCreate={(vals) => {
            createMatterTemplate({
              variables: {
                data: {
                  title: vals.title,
                  description: vals.description,
                  type: outputDesignType,
                  htmlContent: filteredDesigns?.find((x) => x.id === selectedTemplateId)
                    ?.removeTemplateMargin
                    ? `<div style="padding: 30px 96px 30px 96px !important"></div>`
                    : "",
                  active: outputDesignType === OutputDesignType.Proposal ? false : true,
                  groupItem: {
                    connect: {
                      id: vals.groupItemId,
                    },
                  },
                },
              },
              refetchQueries: ["FilteredDesigns", "GroupItems"],
              onCompleted: ({ createDesign: { id } }) => {
                setOpenModal(false);
                setSelectedTemplateId(id);
                setSelectedGroupItemId([vals.groupItemId]);
                setTemplateString(
                  filteredDesigns?.find((x) => x.id === selectedTemplateId)?.removeTemplateMargin
                    ? `<div style="padding: 30px 96px 30px 96px !important"></div>`
                    : ""
                );
              },
            });
          }}
          onEdit={(vals) =>
            editingTemplateId &&
            updateMatterTemplate({
              variables: {
                designType: outputDesignType,
                data: {
                  data: {
                    title: { set: vals.title },
                    description: { set: vals.description },
                    groupItem: {
                      connect: {
                        id: vals.groupItemId,
                      },
                    },
                  },
                  id: editingTemplateId,
                },
              },
              refetchQueries: ["FilteredDesigns", "GroupItems"],
              onCompleted: () => {
                setOpenModal(false);
                setIsEditMode(false);
                setInitialValues(null);
              },
            })
          }
          groupedEntityType={groupedEntityType}
          isEditMode={isEditMode}
          initialValues={initialValues}
        />
        <PracticeAreaDefaultModal
          visible={practiceAreaDefaultModalVisible}
          onCancel={() => setPracticeAreaDefaultModalVisible(false)}
          onSubmit={handlePracticeAreaDefaultSubmit}
          defaultForOrgUnitIds={defaultForOrgUnitIds}
          practiceAreas={practiceAreas}
        />
      </div>
    </>
  );
};
